import ReactNativeAsyncStorage from "@react-native-async-storage/async-storage";
import { getAnalytics, logEvent } from "firebase/analytics";
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  getReactNativePersistence,
  initializeAuth,
  isSignInWithEmailLink,
  linkWithCredential,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";
import { disableNetwork, getFirestore } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  onMessage,
} from "firebase/messaging";
import { getPerformance } from "firebase/performance";
import { getRemoteConfig } from "firebase/remote-config";
import { getStorage } from "firebase/storage";
import { Platform } from "react-native";
import { sendMessageToReactNative } from "../native/webapplogic";
import { MessageType } from "../native/webviewtypes";
import { log } from "../ui/log";
import type { SuperUserModelData } from "../users/SuperUserModel";
import { deleteInLS, getStringInLS, saveStringInLS } from "./localStorage";
import { trackEvent } from "./trackEvent";
import { isRunningInApp } from "./utils";

export const onAuthStateChangedWeb = onAuthStateChanged;

export type VerifyResponse = {
  status: boolean;
  type: "signin" | "register";
  email?: string;
};

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyDoHaWmlWQg5BRzYjirNTVdWLEClxnN5FY",
  authDomain: "planira-1337.firebaseapp.com",
  projectId: "planira-1337",
  storageBucket: "planira-1337.appspot.com",
  messagingSenderId: "735012649237",
  appId: "1:735012649237:web:90810da0768dc7e034511c",
  measurementId: "G-Y218JCPJ88",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);

if (Platform.OS !== "web") {
  initializeAuth(app, {
    persistence: getReactNativePersistence(ReactNativeAsyncStorage),
  });
}

export const auth = getAuth(app);
export const analytics = getAnalytics(app);
export const perf = getPerformance(app);
export const storage = getStorage();
export const functions = getFunctions(app);
export const remoteConfig = getRemoteConfig(app);

export async function signOutFromApp(redirect = true) {
  if (isRunningInApp()) {
    await signOut(auth);
    sendMessageToReactNative({ type: MessageType.Logout });
  } else {
    await signOut(auth);
    if (redirect) {
      window.location.href = "/";
    }
  }
}

export function trackPageView(screenName: string) {
  if (__DEV__) {
    log.analytics(screenName);
    return;
  }

  return logEvent(analytics, "screen_view", {
    firebase_screen: screenName,
    firebase_screen_class: screenName,
  });
}

const isSupported =
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

const messaging = (async () => {
  try {
    const isSupportedBrowser = await isMessagingSupported();
    if (isSupportedBrowser) {
      return getMessaging(app);
    }
    console.log("Firebase not supported this browser");
    return null;
  } catch (err) {
    console.log(err);
    return null;
  }
})();

export async function getMessageToken() {
  const messagingResolve = await messaging;
  if (messagingResolve) {
    return getToken(messagingResolve, {
      vapidKey:
        "BOdiZDYiWguUNBVm1otxQXdderE68YO7XIigt7vecUeW2w6Wg_DQmWf5vsinIp-aLSyljU0NMtBA2sZ47CkYBqg",
    });
  }
}

export const accountSignIn = async (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);

export const accountRegister = async (email: string, password: string) => {
  const currentUser = auth.currentUser;

  if (currentUser?.isAnonymous) {
    // Link the anonymous user with the new email/password credential
    const credential = EmailAuthProvider.credential(
      email.trim(),
      password.trim(),
    );

    try {
      return await linkWithCredential(currentUser, credential);
    } catch (error) {
      console.error("Error linking anonymous user:", error);
      throw error; // Re-throw error to handle it in the calling function
    }
  } else {
    // Register a new account with email and password if not anonymous
    return createUserWithEmailAndPassword(auth, email, password);
  }
};

export const accountForgotPassword = async (email: string) =>
  sendPasswordResetEmail(auth, email);

export const accountVerifyPasswordCode = async (code: string) =>
  verifyPasswordResetCode(auth, code);

export const accountSetPassword = async (code: string, password: string) =>
  confirmPasswordReset(auth, code, password);

export async function isPushSupported(): Promise<boolean> {
  return isSupported;
}

export function isPushEnabled() {
  return isSupported && Notification.permission === "granted";
}

export function hasRequestedForPush() {
  return (
    (isSupported && Notification.permission !== "default") ||
    getStringInLS("push-hide") !== undefined
  );
}

export async function requestPushPermission() {
  console.log("Requesting permission...");
  const permission = await Notification.requestPermission();

  console.log("Got permission", permission);
  return permission === "granted";
}

export async function testPushCall(userUid: string) {
  log.dev("Test Push call triggered", userUid);
  const testPushFlow = httpsCallable(functions, "testPushFlow");
  testPushFlow({ userUid, isDev: __DEV__ }).then((result) => {
    // Read result of the Cloud Function.
    /** @type {any} */
    const _data = result.data;
    log.dev("Test Push response", result);
    trackEvent("push_test_success");
  });

  // TODO - return when onmessage is recieved, or timeout
}

export const sendEmailVerification = async (
  email: string,
  superUserUid: string,
  userUid = "",
  planUid = "",
) => {
  const actionCodeSettings = {
    // URL you want to redirect back to. The domain (www.example.com) for this
    // URL must be in the authorized domains list in the Firebase Console.
    url: `https://planira.app/plan/${planUid}?t=1&sUidRef=${superUserUid}&planUidRef=${planUid}&userUidRef=${userUid}`,
    // This must be true.
    handleCodeInApp: true,
    /*iOS: {
              bundleId: 'com.example.ios'
            },
            android: {
              packageName: 'com.example.android',
              installApp: true,
              minimumVersion: '12'
            },
            dynamicLinkDomain: 'example.page.link'
            */
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);

    // The link was successfully sent. Inform the user.
    // Save the email locally so you don't need to ask the user for it again
    // if they open the link on the same device.
    saveStringInLS("emailForSignIn", email);

    trackEvent("verify_email_send_success");

    return true;
  } catch (error) {
    console.error(error);
    trackEvent("verify_email_send_error");
    return false;
  }
};

export const loginAndMaybeLinkAccount = async (
  getSuperUser: (uid: string) => Promise<SuperUserModelData | undefined>,
): Promise<VerifyResponse> => {
  let mode: "signin" | "register" = "signin";

  // Confirm the link is a sign-in with email link.
  if (isSignInWithEmailLink(auth, window.location.href)) {
    // Additional state parameters can also be passed via URL.
    // This can be used to continue the user's intended action before triggering
    // the sign-in operation.
    // Get the email if available. This should be available if the user completes
    // the flow on the same device where they started it.
    let email: string | undefined | null =
      await getStringInLS("emailForSignIn");
    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again. For example:
      email = window.prompt("Please provide your email for confirmation");
    }

    if (email && auth.currentUser) {
      // The client SDK will parse the code from the link for you.

      try {
        const { user } = await signInWithEmailLink(
          auth,
          email,
          window.location.href,
        );

        const superUserData = await getSuperUser(user.uid);

        const isLinkedToAnonymous = !!superUserData?.email; // Already linked since registred email set

        try {
          if (!isLinkedToAnonymous) {
            mode = "register";
            // Construct the email link credential from the current URL.
            /*const credential = EmailAuthProvider.credentialWithLink(
                                      email,
                                      window.location.href,
                                    );

                                    // Link the credential to the current isAnonymous user.
                                    await linkWithCredential(auth.currentUser, credential);*/

            trackEvent("link_email_success");
          }
        } catch (e) {
          trackEvent("link_email_error");
          throw e;
        }

        deleteInLS("emailForSignIn");

        trackEvent("verify_email_success");

        return { status: true, type: mode, email };
      } catch (error) {
        console.error(error);

        trackEvent("verify_email_error");
        throw error;
      }
    }
  }
  return { status: false, type: mode, email: undefined };
};

let _gotSiteErrorStatus = false;

export async function notifySiteError(error: Error) {
  log.dev("Error call triggered", error);

  if (__DEV__) {
    return;
  }

  _gotSiteErrorStatus = true;

  const gotSiteError = httpsCallable(functions, "gotSiteError");
  gotSiteError({
    errorName: error.name,
    errorMessage: error.message,
    errorStack: error.stack,
    isDev: __DEV__,
  })
    .then((result) => {
      // Read result of the Cloud Function.
      /** @type {any} */
      const _data = result.data;
      log.dev("Got Site response", result);
    })
    .finally(() => {
      disableNetwork(db);
    });

  // TODO - return when onmessage is recieved, or timeout
}

export async function devGetPlaniras(amount = 5, queryType = "recent") {
  const devGetPlanirasProd = httpsCallable(functions, "devGetPlanirasProd");
  return devGetPlanirasProd({
    amount,
    queryType,
  }).then((result) => {
    // @ts-expect-error
    return result.data.planUids;
  });
}

export const onMessageListener = async () =>
  new Promise((_resolve) =>
    (async () => {
      const messagingResolve = await messaging;

      if (messagingResolve) {
        onMessage(messagingResolve, (payload) => {
          console.log("Message received foreground, skipping.", payload);
        });
      }
    })(),
  );
void onMessageListener();

/*
let t: NodeJS.Timeout;
window.onblur = async () => {
  t = setTimeout(async () => {
    await disableNetwork(db);
    log.dev("Firestore disabled");
  }, 10000);
};
window.onfocus = async () => {
  if (!gotSiteErrorStatus) {
    clearTimeout(t);
    await enableNetwork(db);
    log.dev("Firestore enabled");
  }
};
*/
